import { AxiosInstance } from 'axios';
import type {
  Ad,
  CreateAdPayload,
  UpdateAdPayload,
  EnrichedAd,
  GetAdsResponse,
} from '@sdk/shared/types/ads.types';
import { handleServiceError } from '@sdk/utils/error-handler';
import { processMerchantStats, processPreCountedReviews } from '@sdk/shared/lib/merchant-stats';
import { Auth } from '@sdk/auth';
import { ConfigService } from '@sdk/config/config.service';
import { Network, Token } from '@sdk/shared/types/config.types';

export interface GetAdsParams {
  merchant?: string;
  page?: number;
  limit?: number;
  token?: string;
  active?: boolean;
  currency?: string;
  network?: string | number | (string | number)[];
  minPrice?: number;
  minAmount?: number;
  minQuantity?: number;
  tokenSymbol?: string;
  paymentMethod?: string;
  type?: 'buy' | 'sell';
  isAutoAd?: boolean;
  orderBy?: string;
  orderDirection?: 'asc' | 'desc';
  timeLimit?: string | number | (string | number)[];
}

export class AdsService {
  private publicAxios: AxiosInstance;
  private configService: ConfigService;
  private auth: Auth;

  constructor(auth: Auth, configService: ConfigService) {
    this.publicAxios = auth.publicAxios;
    this.auth = auth;
    this.configService = configService;
  }

  getAdById = async (adId: string | number) => {
    return handleServiceError<EnrichedAd>(async () => {
      if (!this.configService.networks || !this.configService.tokens) {
        throw new Error('config not initialised with networks or tokens');
      }
      const { data: ad } = await this.auth.protectedAxios.get<Ad>(`/ads/ad/${adId}`);
      console.log('adnnnn', ad);

      const networkDetails = this.configService.networks.find(n => n.id === ad.networkId);
      const tokenDetails = this.configService.tokens?.find(t => t.id === ad.tokenId);
      if (!networkDetails || !tokenDetails) {
        throw new Error('network or token not found for fetching ad');
      }
      const merchantStats = ad.merchantStats ? processMerchantStats(ad.merchantStats) : undefined;
      ad.processedStats = merchantStats;
      if (!ad) throw new Error('Ad not found');
      return {
        ...ad,
        tokenDetails,
        networkDetails,
      };
    });
  };

  getAds = async (params?: GetAdsParams, forMerchant = false) => {
    return handleServiceError<GetAdsResponse>(async () => {
      const transformedParams = Object.fromEntries(
        Object.entries({
          ...params,
          type: forMerchant ? (params?.type === 'buy' ? 'sell' : 'buy') : params?.type,
          network: Array.isArray(params?.network)
            ? params.network.map(n => n.toString()).join(',')
            : params?.network?.toString(),
          timeLimit: Array.isArray(params?.timeLimit)
            ? params.timeLimit.map(t => parseInt(String(t))).join(',')
            : params?.timeLimit
            ? parseInt(String(params.timeLimit))
            : undefined,
        }).filter(([_, value]) => value !== undefined),
      );
      const limit = params?.limit || 10;
      const page = params?.page || 1;
      const { data: paginatedAds } = await this.publicAxios.get<Ad[]>('/ads', {
        params: {
          ...transformedParams,
          limit,
          page,
        },
      });
      if (!paginatedAds || !Array.isArray(paginatedAds)) throw new Error('Failed to fetch ads');
      const hasMore = paginatedAds.length === limit;
      const networks = this.configService.networks;
      const tokens = this.configService.tokens;
      if (!networks || !tokens) {
        throw new Error('config not initialised');
      }
      if (!networks) throw new Error('Failed to fetch networks');
      const networkMap = networks.reduce((acc, network) => {
        acc[network.id] = network;
        return acc;
      }, {} as Record<number, Network>);
      const tokenMap = tokens.reduce((acc, result) => {
        if (result) {
          acc[result.id] = result;
        }
        return acc;
      }, {} as Record<string, Token>);
      const enrichedAds = paginatedAds.map(ad => {
        const stats = ad.merchantStats ? processMerchantStats(ad.merchantStats) : undefined;
        const reviewStats = ad.merchantReviews
          ? processPreCountedReviews(ad.merchantReviews)
          : undefined;
        return {
          ...ad,
          paymentMethods: Array.isArray(ad.paymentMethods)
            ? ad.paymentMethods.filter(
                (method, index, self) => index === self.findIndex(m => m.id === method.id),
              )
            : [],
          tokenDetails: tokenMap?.[ad.tokenId] || [],
          networkDetails: networkMap[ad.networkId],
          ordersCount: stats?.totalOrders || 0,
          completionRate: parseFloat(stats?.completionRate?.replace('%', '') || '0'),
          responseTime: stats?.avgReleaseTime || '0',
          positiveRates: reviewStats?.positiveRate || 0,
        };
      });
      const total = hasMore ? page * limit + 1 : (page - 1) * limit + enrichedAds.length;
      return {
        data: enrichedAds,
        total,
        hasMore,
      };
    });
  };

  createAd = async (payload: CreateAdPayload) => {
    return handleServiceError(async () => {
      const { data } = await this.auth.protectedAxios.post<Ad>('/ads/ad', payload);
      if (!data) throw new Error('Failed to create ad');
      return data;
    });
  };

  updateAd = async (adId: number | string, payload: UpdateAdPayload) => {
    return handleServiceError(async () => {
      const { data } = await this.auth.protectedAxios.post<Ad>(`/ads/ad/${adId}`, payload);
      if (!data) throw new Error('Failed to update ad');
      return data;
    });
  };

  updateAdStatus = async (adId: number | string, isActive: boolean) => {
    return handleServiceError(async () => {
      const { data } = await this.auth.protectedAxios.post<Ad>(`/ads/ad/${adId}`, {
        isActive: isActive,
      });
      if (!data) throw new Error('Failed to update ad status');
      return data;
    });
  };

  deleteAd = async (adId: number | string) => {
    return handleServiceError(async () => {
      const { data } = await this.auth.protectedAxios.delete<{ success: boolean }>(
        `/ads/ad/${adId}`,
      );
      if (!data) throw new Error('Failed to delete ad');
      return data;
    });
  };
}
